import { BlocoCampos, CampoArea } from '../componentes/campos';
import { ListaEditavel, ListaTextos } from '../componentes/lista-editavel';
import {
  CATALOGO_ENERGIAS,
  CATALOGO_EPC,
  CATALOGO_EPI,
  CATALOGO_PRECAUCOES,
  CATALOGO_TREINAMENTOS,
  CATALOGO_USOS_PROIBIDOS,
} from '../manual-catalogos';
import {
  novaEmergencia,
  novaEnergia,
  novaFuncaoSeguranca,
  novaSinalizacao,
  novoPerigo,
  novoTexto,
} from '../manual-defaults';
import type {
  ItemEmergencia,
  ItemEnergia,
  ItemFuncaoSeguranca,
  ItemPerigo,
  ItemSinalizacao,
} from '../manual-types';
import type { PropsSecao } from './tipos';

export function SecaoSeguranca({ editor }: PropsSecao) {
  const { dados, definirCampo, adicionarItem, adicionarItens, atualizarItem, removerItem, moverItem } = editor;

  return (
    <>
      <BlocoCampos titulo="Uso devido" descricao="Item 2.1 — finalidade para a qual a máquina foi projetada e é utilizada.">
        <CampoArea
          rotulo="Aplicação e uso devido"
          obrigatorio
          linhas={4}
          valor={dados.usoDevido}
          placeholder="Ex.: A máquina destina-se exclusivamente à prensagem de buchas metálicas em componentes..."
          aoMudar={(valor) => definirCampo('usoDevido', valor)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Aplicações proibidas" descricao="Item 2.2 — usos expressamente vedados.">
        <ListaTextos
          itens={dados.usosProibidos}
          multilinha
          placeholder="Ex.: Utilizar a máquina para conformação de peças fora da capacidade nominal."
          rotuloAdicionar="Adicionar proibição"
          vazio="Nenhuma aplicação proibida cadastrada."
          aoAdicionar={() => adicionarItem('usosProibidos', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('usosProibidos', id, patch)}
          aoRemover={(id) => removerItem('usosProibidos', id)}
          aoMover={(id, direcao) => moverItem('usosProibidos', id, direcao)}
          sugestoes={CATALOGO_USOS_PROIBIDOS}
          aoEscolherSugestao={(valor) => adicionarItem('usosProibidos', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Precauções gerais" descricao="Item 2.3 — regras permanentes de conduta na máquina.">
        <ListaTextos
          itens={dados.precaucoes}
          multilinha
          placeholder="Ex.: Verificar o funcionamento da parada de emergência antes de iniciar o turno."
          rotuloAdicionar="Adicionar precaução"
          vazio="Nenhuma precaução cadastrada."
          aoAdicionar={() => adicionarItem('precaucoes', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('precaucoes', id, patch)}
          aoRemover={(id) => removerItem('precaucoes', id)}
          aoMover={(id, direcao) => moverItem('precaucoes', id, direcao)}
          sugestoes={CATALOGO_PRECAUCOES}
          aoEscolherSugestao={(valor) => adicionarItem('precaucoes', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Proteções e equipamentos" descricao="Itens 2.4 e 2.5 — EPC instalados na máquina e EPI obrigatórios.">
        <ListaTextos
          titulo="EPC — proteção coletiva"
          itens={dados.epcs}
          placeholder="Ex.: Proteção móvel intertravada na zona de prensagem"
          rotuloAdicionar="Adicionar EPC"
          vazio="Nenhum EPC cadastrado."
          aoAdicionar={() => adicionarItem('epcs', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('epcs', id, patch)}
          aoRemover={(id) => removerItem('epcs', id)}
          aoMover={(id, direcao) => moverItem('epcs', id, direcao)}
          sugestoes={CATALOGO_EPC}
          aoEscolherSugestao={(valor) => adicionarItem('epcs', novoTexto(valor))}
        />
        <ListaTextos
          titulo="EPI — proteção individual"
          itens={dados.epis}
          placeholder="Ex.: Óculos de segurança"
          rotuloAdicionar="Adicionar EPI"
          vazio="Nenhum EPI cadastrado."
          aoAdicionar={() => adicionarItem('epis', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('epis', id, patch)}
          aoRemover={(id) => removerItem('epis', id)}
          aoMover={(id, direcao) => moverItem('epis', id, direcao)}
          sugestoes={CATALOGO_EPI}
          aoEscolherSugestao={(valor) => adicionarItem('epis', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Treinamentos exigidos" descricao="Item 2.6 — capacitação obrigatória antes da autorização formal.">
        <ListaTextos
          itens={dados.treinamentos}
          placeholder="Ex.: NR-12 — Segurança no trabalho em máquinas"
          rotuloAdicionar="Adicionar treinamento"
          vazio="Nenhum treinamento cadastrado."
          aoAdicionar={() => adicionarItem('treinamentos', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('treinamentos', id, patch)}
          aoRemover={(id) => removerItem('treinamentos', id)}
          aoMover={(id, direcao) => moverItem('treinamentos', id, direcao)}
          sugestoes={CATALOGO_TREINAMENTOS}
          aoEscolherSugestao={(valor) => adicionarItem('treinamentos', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Sinalização aplicada" descricao="Item 2.7 — placas, pictogramas e sinalização luminosa presentes na máquina.">
        <ListaEditavel<ItemSinalizacao>
          itens={dados.sinalizacao}
          colunas={[
            { chave: 'tipo', rotulo: 'Sinalização', placeholder: 'Ex.: Placa de risco elétrico' },
            { chave: 'local', rotulo: 'Local de aplicação', placeholder: 'Ex.: Porta do painel' },
            { chave: 'significado', rotulo: 'Significado', placeholder: 'Ex.: Risco de choque elétrico', largura: '1.4fr' },
          ]}
          rotuloAdicionar="Adicionar sinalização"
          vazio="Nenhuma sinalização cadastrada."
          aoAdicionar={() => adicionarItem('sinalizacao', novaSinalizacao())}
          aoAtualizar={(id, patch) => atualizarItem('sinalizacao', id, patch)}
          aoRemover={(id) => removerItem('sinalizacao', id)}
          aoMover={(id, direcao) => moverItem('sinalizacao', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos
        titulo="Fontes de energia"
        descricao="Itens 2.8 a 2.10 — alimentação, ponto de corte e procedimento de bloqueio."
        acoes={
          <button
            type="button"
            className="manual-ghost-button"
            onClick={() => adicionarItens('energias', CATALOGO_ENERGIAS.slice(0, 3).map((tipo) => novaEnergia({ tipo })))}
          >
            Adicionar energias usuais
          </button>
        }
      >
        <ListaEditavel<ItemEnergia>
          itens={dados.energias}
          colunas={[
            { chave: 'tipo', rotulo: 'Fonte', tipo: 'selecao', opcoes: CATALOGO_ENERGIAS.map((energia) => ({ valor: energia, rotulo: energia })), largura: '0.9fr' },
            { chave: 'especificacao', rotulo: 'Especificação', placeholder: 'Ex.: 440 V / 60 Hz' },
            { chave: 'ponto', rotulo: 'Ponto de corte', placeholder: 'Ex.: Seccionadora QF-01' },
            { chave: 'desconexao', rotulo: 'Procedimento de bloqueio', placeholder: 'Ex.: Bloquear e etiquetar (LOTO)', largura: '1.4fr' },
          ]}
          rotuloAdicionar="Adicionar fonte de energia"
          vazio="Nenhuma fonte de energia cadastrada."
          aoAdicionar={() => adicionarItem('energias', novaEnergia())}
          aoAtualizar={(id, patch) => atualizarItem('energias', id, patch)}
          aoRemover={(id) => removerItem('energias', id)}
          aoMover={(id, direcao) => moverItem('energias', id, direcao)}
        />
        <CampoArea
          rotulo="Conexão à energia (2.9)"
          linhas={3}
          valor={dados.conexaoEnergia}
          placeholder="Texto padrão do template — ajuste conforme o procedimento da unidade."
          aoMudar={(valor) => definirCampo('conexaoEnergia', valor)}
        />
        <CampoArea
          rotulo="Desconexão da energia (2.10)"
          linhas={3}
          valor={dados.desconexaoEnergia}
          placeholder="Texto padrão do template — ajuste conforme o procedimento da unidade."
          aoMudar={(valor) => definirCampo('desconexaoEnergia', valor)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Funções de segurança" descricao="Item 2.11 — dispositivos que compõem o sistema de comando de segurança.">
        <ListaEditavel<ItemFuncaoSeguranca>
          itens={dados.funcoesSeguranca}
          colunas={[
            { chave: 'denominacao', rotulo: 'Função', placeholder: 'Ex.: Parada de emergência' },
            { chave: 'componente', rotulo: 'Componente', placeholder: 'Ex.: Botão tipo cogumelo + relé' },
            { chave: 'categoria', rotulo: 'Categoria / PL', placeholder: 'Ex.: Cat. 3 / PL d', largura: '0.7fr' },
            { chave: 'descricao', rotulo: 'Descrição', placeholder: 'Ex.: Interrompe o ciclo e remove a energia dos atuadores', largura: '1.6fr', tipo: 'area' },
          ]}
          rotuloAdicionar="Adicionar função de segurança"
          vazio="Nenhuma função de segurança cadastrada."
          aoAdicionar={() => adicionarItem('funcoesSeguranca', novaFuncaoSeguranca())}
          aoAtualizar={(id, patch) => atualizarItem('funcoesSeguranca', id, patch)}
          aoRemover={(id) => removerItem('funcoesSeguranca', id)}
          aoMover={(id, direcao) => moverItem('funcoesSeguranca', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Matriz de perigos" descricao="Item 2.12 — perigos indissociáveis permanecem após as proteções; dissociáveis são controlados por elas.">
        <ListaEditavel<ItemPerigo>
          itens={dados.perigos}
          colunas={[
            { chave: 'local', rotulo: 'Local', placeholder: 'Ex.: Zona de prensagem', largura: '0.9fr' },
            { chave: 'fonte', rotulo: 'Fonte', placeholder: 'Ex.: Movimento do cilindro' },
            { chave: 'perigo', rotulo: 'Perigo', placeholder: 'Ex.: Esmagamento de mãos', largura: '1.2fr' },
            { chave: 'classe', rotulo: 'Classe', tipo: 'selecao', largura: '0.8fr', opcoes: [
              { valor: 'dissociavel', rotulo: 'Dissociável' },
              { valor: 'indissociavel', rotulo: 'Indissociável' },
            ] },
            { chave: 'protecao', rotulo: 'Proteção / controle', placeholder: 'Ex.: Cortina de luz + comando bimanual', largura: '1.4fr', tipo: 'area' },
          ]}
          rotuloAdicionar="Adicionar perigo"
          vazio="Nenhum perigo cadastrado."
          aoAdicionar={() => adicionarItem('perigos', novoPerigo())}
          aoAtualizar={(id, patch) => atualizarItem('perigos', id, patch)}
          aoRemover={(id) => removerItem('perigos', id)}
          aoMover={(id, direcao) => moverItem('perigos', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Emergências" descricao="Item 2.13 — situações previstas e ação imediata esperada.">
        <ListaEditavel<ItemEmergencia>
          itens={dados.emergencias}
          colunas={[
            { chave: 'situacao', rotulo: 'Situação', placeholder: 'Ex.: Pessoa presa na zona de trabalho' },
            { chave: 'acao', rotulo: 'Ação imediata', placeholder: 'Ex.: Acionar a parada de emergência e bloquear as energias', largura: '1.8fr', tipo: 'area' },
          ]}
          rotuloAdicionar="Adicionar situação"
          vazio="Nenhuma situação de emergência cadastrada."
          aoAdicionar={() => adicionarItem('emergencias', novaEmergencia())}
          aoAtualizar={(id, patch) => atualizarItem('emergencias', id, patch)}
          aoRemover={(id) => removerItem('emergencias', id)}
          aoMover={(id, direcao) => moverItem('emergencias', id, direcao)}
        />
      </BlocoCampos>
    </>
  );
}
